import React from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@mui/material";
import { Stack } from "@mui/system";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import { ErrorAlertWithTranslation } from "../../../Api/Service/ApiErrorDisplay";
import { updateUserPassword } from "../../../Api/User/User.Api";
import { useAuth } from "../../../Store/AuthContext";
import useLoadingState from "../../../Utils/Hooks/useLodingState";
import useOverlayState from "../../../Utils/Hooks/useOverlayState";
import { SideDrawer } from "../../atoms/Drawer/SideDrawer";
import { PasswordInput } from "../../atoms/Input/PasswordInput";
import { LoadingButtonType } from "../../atoms/LoadingButton/LoadingButton";
import { PasswordStrengthField } from "../../molecules/PasswordStrengthField/PasswordStrengthField";
import { FormItemsWrap } from "../../molecules/Wrap/Wrap";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";

export const ChangePassword = ({ close, openDrawer }) => {
  const { state } = useAuth();
  const { t } = useTranslation();
  const { startLoading, LoadingState, stopLoading } = useLoadingState();
  const { closeOverlay, OverlayState, openOverlay } = useOverlayState();

  const {
    control,
    watch,
    getValues,
    reset,
    handleSubmit,
    setError,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    resolver: yupResolver(
      object({
        currentPassword: string().required(t("validation:required-field")),
        newPassword: string()
          .required(t("validation:required-field"))
          .test("passwordCriteria", "", function (value) {
            // true means validated
            let flag = true;
            const trimmedValue = value.trim().replace(/\s/g, "");
            const isValidPassword =
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(trimmedValue);
            if (!isValidPassword) {
              // false means rules violated
              flag = false;
            }
            return flag;
          })
          .test(
            "testPasswordMatching",
            t("validation:same-password"),
            function (value) {
              let flag = true;
              if (value === getValues("currentPassword")) {
                flag = false;
              }
              return flag;
            },
          ),
        confirmPassword: string()
          .required(t("validation:required-field"))
          .test(
            "testPasswordMatching",
            t("validation:password-do-not-match"),

            function (value) {
              let flag = true;
              if (value !== getValues("newPassword")) {
                flag = false;
              }
              return flag;
            },
          ),
      }),
    ),
    mode: "onChange",
  });

  const onSubmit = async (formData) => {
    try {
      startLoading({ btnName: "save-password" });
      await updateUserPassword({
        data: { ...formData, email: state?.user?.email },
      }).then((response) => {
        if (response.type === 0) {
          openOverlay({ OverlayName: "success" });
          reset();
        } else if (response.type === 2) {
          if (response.message === "invalid-user") {
            ErrorAlertWithTranslation({
              code: "server-error",
              translation: t,
            });
          } else if (response.message === "same-password") {
            setError("newPassword", {
              message: t(response.message, { ns: "validation" }),
            });
          } else if (response.message === "invalid-password") {
            setError("currentPassword", {
              message: t(response.message, { ns: "validation" }),
            });
          } else if (response.message === "password-update-failed") {
            ErrorAlertWithTranslation({
              code: "server-error",
              translation: t,
            });
          }
        }
      });
      stopLoading();
    } catch (error) {
      console.log("error", error);
    }
  };
  return (
    <SideDrawer
      headingText={t("glossary:settings.change-password")}
      width={"648px"}
      openDrawer={openDrawer}
      close={close}
      anchorDrawer={"right"}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack rowGap={"15px"}>
          <PasswordInput
            hideValidBorderColor={true}
            required={true}
            control={control}
            label={t("glossary:settings.update-password.current")}
            name={"currentPassword"}
          />

          <PasswordStrengthField
            t={t}
            control={control}
            label={t("glossary:settings.update-password.new")}
            name={"newPassword"}
            password={watch().newPassword}
            isInherit={false}
          />
          <PasswordInput
            required={true}
            control={control}
            label={t("glossary:settings.update-password.confirm")}
            name={"confirmPassword"}
          />

          <FormItemsWrap
            direction={"row"}
            justifyContent={"flex-end"}
            gap={"16px"}
          >
            <Button
              disabled={LoadingState.visible}
              variant="whiteSquare"
              onClick={() =>
                reset({
                  confirmPassword: "",
                  currentPassword: "",
                  newPassword: "",
                })
              }
            >
              {t("common:action.clear")}
            </Button>
            <LoadingButtonType
              loading={LoadingState.visible}
              variant="primaryBlue"
              type="submit"
            >
              {t("common:action.save")}
            </LoadingButtonType>
          </FormItemsWrap>
        </Stack>
      </form>
      {OverlayState.visible && (
        <ConfirmDialog
          title={t("glossary:settings.change-password")}
          description={t("validation:password-changed-successfully")}
          open={OverlayState?.visible}
          close={() => {
            closeOverlay();
          }}
          onOkClick={() => {
            close();
            closeOverlay();
          }}
        />
      )}
    </SideDrawer>
  );
};
